import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IdCard, Building2, MapPin, ToggleLeft, Calendar, CalendarRange, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Vencimentos da CNH (WOMOTFCC / ROMOTFCC do ERP) — por data de vencimento ou de emissão da CNH

interface Cnh { motcod: string; nome: string; cargo: string; documento: string; cnh: string; categoria: string; uf: string; emissao: string; validade: string }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function RelatorioVencimentoCnh() {
  const { empresas, empresaCod } = useEmpresa()
  const hoje = isoLocal(new Date())
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [filial, setFilial] = useState('')
  const [situacao, setSituacao] = useState('A')
  const [busca] = useSearchParams() // o cartão CNH do dashboard abre com período, sem afastados e já gerado
  const dataUrl = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(busca.get(k) ?? '') ? busca.get(k)! : '')
  const [dataIni, setDataIni] = useState(dataUrl('inicio') || isoLocal(new Date(Date.now() - 30 * 86400000)))
  const [dataFim, setDataFim] = useState(dataUrl('fim') || hoje)
  const [semAfastados, setSemAfastados] = useState(busca.get('semAfastados') === '1')
  const [tipo, setTipo] = useState<'V' | 'E'>('V')
  const [res, setRes] = useState<Cnh[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  const params = () => ({ empresas: empresasSel.join(','), filial, situacao, inicio: dataIni, fim: dataFim, tipo, semAfastados: semAfastados ? 1 : 0, empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/vencimento-cnh', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => { if (busca.get('auto') === '1') gerar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/vencimento-cnh', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'vencimentos-cnh.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      let msg = formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <IdCard size={18} color="#fff" />
            </span>
            <div>
              <h2>Vencimentos da CNH</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Funcionários com CNH vencendo (ou emitida) no período</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Filial" icon={MapPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={filial} onChange={e => setFilial(e.target.value)} maxLength={2} style={{ maxWidth: 70 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Situação" icon={ToggleLeft}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <select value={situacao} onChange={e => setSituacao(e.target.value)} style={{ maxWidth: 160 }}>
                <option value="A">Ativos</option>
                <option value="I">Inativos</option>
                <option value="">Todos</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={semAfastados} onChange={e => { setSemAfastados(e.target.checked); setRes(null) }} />
                Sem quem está afastado hoje
              </label>
            </div>
          </Linha>

          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>

          <Linha label="Por Data de" icon={CalendarRange}>
            <select value={tipo} onChange={e => setTipo(e.target.value as 'V' | 'E')} style={{ maxWidth: 200 }}>
              <option value="V">Data Vencimento</option>
              <option value="E">Data Emissão</option>
            </select>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhuma CNH no período.</div>}

            {res.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Cargo/Função</th><th style={th}>CPF/CNPJ</th>
                      <th style={th}>CNH</th><th style={th}>Cat.</th><th style={th}>UF</th><th style={th}>Emissão</th><th style={th}>Validade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.map((l, i) => {
                      const vencida = !!l.validade && l.validade < hoje
                      return (
                        <tr key={l.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{l.nome}</td>
                          <td style={td}>{l.cargo}</td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{l.documento}</td>
                          <td style={td}>{l.cnh}</td>
                          <td style={td}>{l.categoria}</td>
                          <td style={td}>{l.uf}</td>
                          <td style={td}>{dataBr(l.emissao)}</td>
                          <td style={{ ...td, fontWeight: 700, color: vencida ? '#b91c1c' : '#1b3f7b' }}>{dataBr(l.validade)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
              TOTAL DE REGISTROS: {res.length}
            </div>
            {res.length > 0 && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Validade em vermelho = CNH já vencida.</div>}
          </div>
        </div>
      )}
    </div>
  )
}
